import { designActionTypes, userActionTypes } from '../actions/types';
import { addChanges, getUndo, getRedo } from '../reducers/design.reducer';
import { designService, resourceService, templateService } from '../services';
import {
  EditorState,
  ContentState,
  convertFromHTML,
  convertToRaw,
  convertFromRaw,
  RichUtils,
  SelectionState,
  Modifier,
} from 'draft-js';
import { extractInlineStyle, getCustomStyleMap, toggleCustomInlineStyle } from 'draftjs-utils';
import _ from 'lodash';

let saveRequestSequence = 0;

const startSave = (dispatch, getState, type) => {
  // Serializing saves also prevents an older request overwriting a newer payload on the API.
  if (getState().design.saving) return null;
  const requestId = `save-${++saveRequestSequence}`;
  dispatch({ type, requestId });
  return requestId;
};

const failSave = (dispatch, getState, type, requestId, error) => {
  if (_.get(getState(), 'design.pendingSave.requestId') !== requestId) return;
  dispatch({ type, requestId, error });
  const authAction = _refreshTokenOnFailure(
    error || {}, type, getState().authentication.pendingRefreshingToken,
  );
  if (authAction.type !== type) dispatch(authAction);
};

const resetState = () => {
  return dispatch => dispatch({ type: designActionTypes.RESET_STATE });
};

const create = design => {
  return (dispatch, getState) => {
    dispatch(request(design.templateCategory));

    designService.create(getState().authentication.user.userId, design).then(
      design => {
        const parsedDesign = _parseDesign(design);
        dispatch(success(design._id, ...parsedDesign, design.templateCategory));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            designActionTypes.CREATE_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request(category) {
    return { type: designActionTypes.CREATE_REQUEST, category };
  }
  function success(
    designId,
    name,
    width,
    height,
    pages,
    entitiesState,
    editorsState,
    svgsState,
    imageState,
    fontsLoaded,
    category,
  ) {
    return {
      type: designActionTypes.CREATE_SUCCESS,
      designId,
      name,
      width,
      height,
      pages,
      entitiesState,
      editorsState,
      imageState,
      svgsState,
      fontsLoaded,
      category,
    };
  }
};

const duplicate = (designId, index) => {
  return (dispatch, getState) => {
    dispatch(request());
    const designs = [...getState().design.designs];

    designService.duplicate(getState().authentication.user.userId, designId).then(
      design => {
        designs.splice(index + 1, 0, design);
        dispatch(success(designs));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            designActionTypes.DUPLICATE_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: designActionTypes.DUPLICATE_REQUEST };
  }
  function success(designs) {
    return {
      type: designActionTypes.DUPLICATE_SUCCESS,
      designs,
    };
  }
};

const save = previewPages => {
  return (dispatch, getState) => {
    const requestId = startSave(dispatch, getState, designActionTypes.SAVE_REQUEST);
    if (!requestId) return;

    const spacingStyleClasses = getState().design.spacingStyleClasses;
    const listBlockStyleClasses = getState().design.listBlockStyleClasses;
    previewPages = previewPages.map(page => {
      return `<style>${Object.values(spacingStyleClasses).join()}${Object.values(
        listBlockStyleClasses,
      ).join()}</style>${page}`;
    });

    const user = getState().authentication.user;
    let userId = null;
    if (user.role === 'client') {
      userId = user.userId;
    }

    const designObj = _packDesign(getState().design.designId, getState().design);

    return designService.update(userId, { ...designObj, templateCategory: getState().selectedCategory, previewPages }).then(
      response => {
        dispatch(success(requestId, response && response.previewGeneration));
      },
      error => {
        failSave(dispatch, getState, designActionTypes.SAVE_FAILURE, requestId, error);
      },
    );
  };

  function success(requestId, previewGeneration) {
    return { type: designActionTypes.SAVE_SUCCESS, requestId, previewGeneration };
  }
};

const saveAsTemplate = (previewPages, extension) => {
  return (dispatch, getState) => {
    const requestId = startSave(dispatch, getState, designActionTypes.SAVE_AS_TEMPLATE_REQUEST);
    if (!requestId) return;

    const designObj = _packDesign(getState().template.templateId, getState().design);
    if (extension) designObj.extension = extension;

    const spacingStyleClasses = getState().design.spacingStyleClasses;
    const listBlockStyleClasses = getState().design.listBlockStyleClasses;
    previewPages = previewPages.map(page => {
      return `<style>${Object.values(spacingStyleClasses).join()}${Object.values(
        listBlockStyleClasses,
      ).join()}</style>${page}`;
    });

    return templateService.update({ ...designObj, previewPages }).then(
      response => {
        dispatch(success(requestId, response && response.previewGeneration));
      },
      error => {
        failSave(dispatch, getState, designActionTypes.SAVE_AS_TEMPLATE_FAILURE, requestId, error);
      },
    );
  };

  function success(requestId, previewGeneration) {
    return { type: designActionTypes.SAVE_AS_TEMPLATE_SUCCESS, requestId, previewGeneration };
  }
};

const saveAsText = (previewPages, extension) => {
  return (dispatch, getState) => {
    const requestId = startSave(dispatch, getState, designActionTypes.SAVE_AS_TEXT_REQUEST);
    if (!requestId) return;

    const designObj = _packDesign(getState().template.templateId, getState().design);
    if (extension) designObj.extension = extension;

    const spacingStyleClasses = getState().design.spacingStyleClasses;
    const listBlockStyleClasses = getState().design.listBlockStyleClasses;
    previewPages = previewPages.map(page => {
      return `<style>${Object.values(spacingStyleClasses).join()}${Object.values(
        listBlockStyleClasses,
      ).join()} .artboard{background: transparent !important;}</style>${page}`;
    });

    const resourceObj = {
      id: designObj.id,
      name: designObj.name,
      data: designObj.pages[0],
      width: designObj.size.width,
      height: designObj.size.height,
    };

    return resourceService.update({ ...resourceObj, previewPages }).then(
      response => {
        dispatch(success(requestId, response && response.previewGeneration));
      },
      error => {
        failSave(dispatch, getState, designActionTypes.SAVE_AS_TEXT_FAILURE, requestId, error);
      },
    );
  };

  function success(requestId, previewGeneration) {
    return { type: designActionTypes.SAVE_AS_TEXT_SUCCESS, requestId, previewGeneration };
  }
};

const fetch = designId => {
  return (dispatch, getState) => {
    dispatch(request());

    const user = getState().authentication.user;
    let userId = null;
    if (user.role === 'client') {
      userId = user.userId;
    }

    designService.find(userId, designId).then(
      design => {
        const parsedDesign = _parseDesign(design);
        dispatch(success(designId, ...parsedDesign, design.templateCategory));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            designActionTypes.FETCH_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: designActionTypes.FETCH_REQUEST };
  }
  function success(
    designId,
    name,
    width,
    height,
    pages,
    entitiesState,
    editorsState,
    svgsState,
    imagesState,
    fontsLoaded,
    category,
  ) {
    return {
      type: designActionTypes.FETCH_SUCCESS,
      designId,
      name,
      width,
      height,
      pages,
      entitiesState,
      editorsState,
      svgsState,
      imagesState,
      fontsLoaded,
      category,
    };
  }
};

const search = (userId, query, clear) => {
  return (dispatch, getState) => {
    dispatch(request());

    let skip = getState().design.designSkip;
    let limit = getState().design.designLimit;

    if (!query.name) {
      delete query.name;
    }

    if (clear || skip === null) {
      skip = 0;
    } else {
      skip = skip + limit;
    }

    designService
      .findAll(userId, {
        ...query,
        skip,
        limit,
      })
      .then(
        designs => {
          dispatch(success(designs, skip, limit, clear));
        },
        error => {
          dispatch(
            _refreshTokenOnFailure(
              error,
              designActionTypes.SEARCH_FAILURE,
              getState().authentication.pendingRefreshingToken,
            ),
          );
        },
      );
  };

  function request() {
    return { type: designActionTypes.SEARCH_REQUEST };
  }
  function success(designs, skip, limit, clear) {
    return { type: designActionTypes.SEARCH_SUCCESS, designs, skip, limit, clear };
  }
};

/**
 * @param editable {string} for edit or use in user design
 */
const loadTemplateDesign = (pageId, template, editable) => {
  return (dispatch, getState) => {
    const parsedDesign = _parseDesign(template);

    if (editable) {
      dispatch(resetState());
    } else {
      parsedDesign[0] = getState().design.name;
    }
    const pages = [...getState().design.pages];
    const entitiesState = getState().design.entitiesState;
    const editorsState = getState().design.editorsState;
    const svgsState = getState().design.svgsState;
    const imagesState = getState().design.imagesState;
    const fontsLoaded = getState().design.fontsLoaded;
    parsedDesign[3].forEach((page, index) => {
      if (index === 0) pages[pageId] = page;
      else pages.splice(pageId + index, 0, page);
    });
    parsedDesign[3] = pages;
    parsedDesign[4] = { ...entitiesState, ...parsedDesign[4] };
    parsedDesign[5] = { ...editorsState, ...parsedDesign[5] };
    parsedDesign[6] = { ...svgsState, ...parsedDesign[6] };
    parsedDesign[7] = { ...imagesState, ...parsedDesign[7] };
    parsedDesign[8] = { ...fontsLoaded, ...parsedDesign[8] };
    dispatch(success(...parsedDesign, editable));

    function success(
      name,
      width,
      height,
      pages,
      entitiesState,
      editorsState,
      svgsState,
      imagesState,
      fontsLoaded,
      saved,
    ) {
      return {
        type: designActionTypes.LOAD_TEMPLATE_DESIGN,
        name,
        width,
        height,
        pages,
        entitiesState,
        editorsState,
        svgsState,
        imagesState,
        fontsLoaded,
        saved,
      };
    }
  };
};

const loadTextDesign = (pageId, entities) => {
  return (dispatch, getState) => {
    const design = {
      name: '', // It's not important
      pages: [{ entities }],
      size: {
        width: 1080, // It's not important
        height: 1080, // It's not important
      },
    };
    const parsedDesign = _parseDesign(design);

    const pages = [...getState().design.pages];
    const entitiesState = getState().design.entitiesState;
    const editorsState = getState().design.editorsState;
    const fontsLoaded = getState().design.fontsLoaded;
    pages[pageId].entities.push(...parsedDesign[3][0].entities);
    parsedDesign[3] = pages;
    parsedDesign[4] = { ...entitiesState, ...parsedDesign[4] };
    parsedDesign[5] = { ...editorsState, ...parsedDesign[5] };
    parsedDesign[8] = { ...fontsLoaded, ...parsedDesign[8] };
    dispatch(success(parsedDesign[3], parsedDesign[4], parsedDesign[5], parsedDesign[8]));

    function success(pages, entitiesState, editorsState, fontsLoaded) {
      return {
        type: designActionTypes.LOAD_TEXT_DESIGN,
        pages,
        entitiesState,
        editorsState,
        fontsLoaded,
      };
    }
  };
};

const fetchTemplateDesign = templateId => {
  return (dispatch, getState) => {
    dispatch(request());

    templateService.findOne(templateId).then(
      template => {
        const parsedDesign = _parseDesign(template);
        dispatch(success(...parsedDesign));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            designActionTypes.FETCH_TEMPLATE_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: designActionTypes.FETCH_TEMPLATE_REQUEST };
  }
  function success(name, width, height, pages, entitiesState, editorsState, svgsState, imagesState, fontsLoaded) {
    return {
      type: designActionTypes.FETCH_TEMPLATE_SUCCESS,
      name,
      width,
      height,
      pages,
      entitiesState,
      editorsState,
      svgsState,
      imagesState,
      fontsLoaded,
    };
  }
};

const fetchTextDesign = resourceId => {
  return (dispatch, getState) => {
    dispatch(request());

    resourceService.findOne(resourceId).then(
      resource => {
        const design = {
          name: resource.name,
          pages: [resource.data || {}],
          size: {
            width: resource.width || 1080,
            height: resource.height || 1080,
          },
        };
        const parsedDesign = _parseDesign(design);
        dispatch(success(...parsedDesign));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            designActionTypes.FETCH_TEXT_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: designActionTypes.FETCH_TEXT_REQUEST };
  }
  function success(name, width, height, pages, entitiesState, editorsState, svgsState, imagesState, fontsLoaded) {
    return {
      type: designActionTypes.FETCH_TEXT_SUCCESS,
      name,
      width,
      height,
      pages,
      entitiesState,
      editorsState,
      svgsState,
      imagesState,
      fontsLoaded,
    };
  }
};

/**
 *
 * @param actionType:
 * @param previousPage:
 * @param {*} actionType add, delete, copy
 * @param {*} pageId
 * @param {Boolean} noCache true for save changes
 * @param {*} previousPage used for add with content
 */
const actionOnPage = (actionType, pageId, noCache, previousPage) => {
  return (dispatch, getState) => {
    const pages = [...getState().design.pages];
    switch (actionType) {
      case 'add':
        if (!noCache)
          addChanges({
            undo: { action: actionOnPage, props: ['delete', pageId + 1, true] },
            redo: { action: actionOnPage, props: [actionType, pageId, true] },
          });
        pages.splice(pageId + 1, 0, previousPage ? previousPage : { entities: [] });
        return dispatch({ type: designActionTypes.ADD_PAGE, pages });
      case 'delete':
        if (!noCache)
          addChanges({
            undo: { action: actionOnPage, props: ['add', pageId - 1, true, pages[pageId]] },
            redo: { action: actionOnPage, props: [actionType, pageId, true] },
          });
        pages.splice(pageId, 1);
        return dispatch({ type: designActionTypes.DELETE_PAGE, pages });
      case 'copy':
        if (!noCache)
          addChanges({
            undo: { action: actionOnPage, props: ['delete', pageId + 1, true] },
            redo: { action: actionOnPage, props: [actionType, pageId, true] },
          });
        // eslint-disable-next-line no-case-declarations
        const [newPages, entitiesState, editorsState, svgsState, imagesState] = _duplicatePage(
          pages[pageId],
          getState().design.entitiesState,
          getState().design.editorsState,
          getState().design.svgsState,
          getState().design.imagesState,
        );
        pages.splice(pageId + 1, 0, newPages[0]);

        return dispatch({
          type: designActionTypes.COPY_PAGE,
          pages,
          entitiesState,
          editorsState,
          svgsState,
          imagesState,
        });
      default:
        console.log(actionType);
        break;
    }
  };
};

/**
 *
 * @param {*} pageId
 * @param {Object} entities {entities: [{id, type},...], states: {id:entity,...}}
 * @param {Boolean} noCache true for save changes
 */
const addEntity = (pageId, entities, noCache) => {
  return (dispatch, getState) => {
    const pages = [...getState().design.pages];
    let addedEntities = [];
    const extraProps = {};

    if (entities.entities[0].type === 'Text' && !noCache) extraProps.enabledEditorId = entities.entities[0].id;

    entities.entities.forEach(entity => {
      if (_.has(entities, `positions.${entity.id}`)) {
        pages[pageId].entities.splice(entities.positions[entity.id], 0, entity);
      } else {
        pages[pageId].entities.push(entity);
      }
    });
    addedEntities = Object.keys(entities.states);

    if (!noCache)
      addChanges({
        undo: { action: deleteEntity, props: [pageId, addedEntities, true] },
        redo: { action: addEntity, props: [pageId, entities, true] },
      });
    return dispatch({
      type: designActionTypes.ADD_ENTITY,
      pages,
      entitiesState: entities.states,
      selectedEntity: entities.entities[0],
      ...extraProps,
    });
  };
};

/**
 *
 * @param {*} pageId
 * @param {Array} entityIds
 * @param {Boolean} noCache true for save changes
 */
const deleteEntity = (pageId, entityIds, noCache) => {
  return (dispatch, getState) => {
    try {
      const pages = [...getState().design.pages];
      const entitiesState = { ...getState().design.entitiesState };
      let deletedEntities = { entities: [], states: {}, positions: {} };

      if (
        entityIds.length === 1 &&
        entitiesState[entityIds[0]].type === 'Frame' &&
        _.has(entitiesState[entityIds[0]], 'data.medias')
      ) {
        const entityId = entityIds[0];
        const entitiesState = { ...getState().design.entitiesState };
        if (!noCache) {
          const data = { medias: entitiesState[entityId].data.medias };
          addChanges({
            undo: { action: changeFrameData, props: [pageId, entityId, data, true] },
            redo: { action: deleteEntity, props: [pageId, entityId, true] },
          });
        }
        delete entitiesState[entityId].data.medias;
        return dispatch({ type: designActionTypes.DELETE_FRAME_MEDIA, entitiesState });
      } else {
        entityIds.forEach(entityId => {
          deletedEntities.entities.push({
            id: parseInt(entityId),
            type: entitiesState[entityId].type,
          });
          deletedEntities.states[entityId] = entitiesState[entityId];

          delete entitiesState[entityId];
        });
        _.remove(pages[pageId].entities, (entity, index) => {
          if (entityIds.indexOf(entity.id.toString()) !== -1) {
            deletedEntities.positions[entity.id.toString()] = index;
            return true;
          }
          return false;
        });

        if (!noCache)
          addChanges({
            undo: { action: addEntity, props: [pageId, deletedEntities, true] },
            redo: { action: deleteEntity, props: [pageId, entityIds, true] },
          });
        return dispatch({ type: designActionTypes.DELETE_ENTITY, pages, entitiesState });
      }
    } catch (err) {
      return () => { };
    }
  };
};

/**
 *
 * @param {*} pageId
 * @param {Array} entityIds
 * @param {Boolean} noCache true for save changes
 */
const groupEntities = (pageId, entityIds, groupsCount, noCache) => {
  console.log('>> Start Design Actions Group Entities');
  return (dispatch, getState) => {
    try {
      console.log('>> Start Try');
      const pages = [...getState().design.pages];
      const entitiesState = { ...getState().design.entitiesState };
      console.log('pages 0', pages);
      console.log('entitiesState 0', entitiesState);

      const groupId = `G_${groupsCount + 1}`;
      console.log('groupId', groupId);
      const groupedEntities = [];
      entityIds.forEach(entityId => {
        const entity = _.find(entitiesState, { keyId: entityId });
        groupedEntities.push({ id: entityId, type: entity.type });
        console.log('entity', entity);
        console.log('groupedEntities', groupedEntities);
      });

      entityIds.forEach(entityId => {
        console.log('entityId forEach', entityId);
        const pageEntityIndex = _.findIndex(pages[pageId].entities, { id: entityId });
        console.log('pageEntityIndex forEach', pageEntityIndex);
        pages[pageId].entities[pageEntityIndex] = {
          ...pages[pageId].entities[pageEntityIndex],
          groupId: groupId,
          entities: groupedEntities,
        };
        const entityIndex = _.findIndex(entitiesState, { keyId: entityId });
        entitiesState[entityIndex] = { ...entitiesState[entityIndex], groupId: groupId };
      });

      console.log('End');
      console.log('pages', pages);
      console.log('entitiesState', entitiesState);
      // if (!noCache)
      // addChanges({
      //   undo: { action: addEntity, props: [pageId, deletedEntities, true] },
      //   redo: { action: deleteEntity, props: [pageId, entityIds, true] },
      // });
      // return dispatch({ type: designActionTypes.DELETE_ENTITY, pages, entitiesState });
    } catch (err) {
      return () => {
        console.log('err', err);
      };
    }
  };
};

/**
 *
 * @param {Number} pageId
 * @param {Boolean} noCache true for save changes
 */
const deletePageBackground = (pageId, noCache) => {
  return (dispatch, getState) => {
    const pages = [...getState().design.pages];

    if (_.has(pages[pageId], 'background.media')) {
      if (!noCache) {
        const { src, type } = pages[pageId].background.media;
        addChanges({
          undo: { action: changeEntity, props: [pageId, 'Background', src, type, true] },
          redo: { action: deletePageBackground, props: [pageId, true] },
        });
      }
      delete pages[pageId].background.media;
      return dispatch({ type: designActionTypes.DELETE_PAGE_BACKGROUND_MEDIA, pages });
    } else {
      if (!noCache)
        addChanges({
          undo: {
            action: changePageBackgroundColor,
            props: [pageId, _.get(pages[pageId], 'background.color', '#fff'), true],
          },
          redo: { action: deletePageBackground, props: [pageId, true] },
        });
      _.set(pages[pageId], 'background.color', '#fff');
      return dispatch({ type: designActionTypes.DELETE_PAGE_BACKGROUND_COLOR, pages });
    }
  };
};

const addNewEditorState = initState => {
  var state;
  if (initState.contentType === 'html') {
    const blocksFromHTML = convertFromHTML(initState.content);
    const initContent = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
    let editorState = EditorState.createWithContent(initContent);
    editorState = _setEditorFontStyle(editorState, initState);
    state = { [initState.id]: editorState };
  } else if (initState.contentType === 'raw') {
    state = { [initState.id]: EditorState.createWithContent(convertFromRaw(initState.content)) };
  } else {
    state = { [initState.id]: EditorState.createEmpty() };
  }

  return dispatch => dispatch({ type: designActionTypes.ADD_EDITOR_STATE, state });
};

const addNewSVGState = initState => {
  var state = { [initState.id]: initState.data };

  return dispatch => dispatch({ type: designActionTypes.ADD_SVG_STATE, state });
};

const addNewColorState = initState => {
  var state;
  return (dispatch, getState) => {
    state = getState().design.imageColorsState;
    state = [...state, initState];
    dispatch({ type: designActionTypes.ADD_IMAGE_COLOR_STATE, state });
  };
};

/**
 *
 * @param {*} newEditorState
 * @param {Boolean} noCache true for save changes
 */
const changeEditorState = (newEditorState, noCache) => {
  return (dispatch, getState) => {
    try {
      const editorId = Object.keys(newEditorState)[0];
      const contentChanged =
        newEditorState[editorId].getUndoStack().size !== getState().design.editorsState[editorId].getUndoStack().size;
      if (!noCache) {
        if (contentChanged)
          addChanges({
            undo: {
              action: changeEditorState,
              props: [
                {
                  [editorId]: getState().design.editorsState[editorId],
                },
                true,
              ],
            },
            redo: { action: changeEditorState, props: [newEditorState, true] },
          });
      }
      // Check editor content, if empty set latest style for new text
      if (!newEditorState[editorId].getCurrentContent().hasText() && newEditorState[editorId].getUndoStack().size > 0) {
        newEditorState[editorId] = EditorState.setInlineStyleOverride(
          newEditorState[editorId],
          newEditorState[editorId]
            .getUndoStack()
            .first()
            .getFirstBlock()
            .getInlineStyleAt(0),
        );
      }
      return dispatch({
        type: designActionTypes.CHANGE_EDITOR_STATE,
        newEditorState,
        saved: !contentChanged,
      });
    } catch (err) {
      return () => { };
    }
  };
};

const changeTextEditorStyle = (editorId, style, noCache) => {
  return (dispatch, getState) => {
    try {
      let newState;
      let editorState = getState().design.editorsState[editorId];
      if (style.all) {
        editorState = _selectAllText(editorState);
      }
      switch (style.type) {
        case 'fontSize':
          newState = toggleCustomInlineStyle(editorState, 'fontSize', style.value);
          break;
        case 'fontFamily':
          newState = toggleCustomInlineStyle(editorState, 'fontFamily', style.value);
          break;
        case 'fontColor':
          newState = toggleCustomInlineStyle(editorState, 'color', style.value);
          break;
        case 'inline':
          newState = RichUtils.toggleInlineStyle(editorState, style.value);
          break;
        case 'list':
          newState = RichUtils.toggleBlockType(editorState, style.value);
          break;
        case 'alignment':
          newState = EditorState.push(
            editorState,
            Modifier.mergeBlockData(editorState.getCurrentContent(), editorState.getSelection(), {
              'text-align': style.value,
            }),
            'change-block-data',
          );
          break;
        case 'uppercase':
          newState = EditorState.push(
            editorState,
            Modifier.mergeBlockData(editorState.getCurrentContent(), editorState.getSelection(), {
              'text-transform': style.value,
            }),
            'change-block-data',
          );
          break;
        default:
          break;
      }
      if (newState) {
        if (!noCache)
          addChanges({
            undo: {
              action: changeEditorState,
              props: [
                {
                  [editorId]: getState().design.editorsState[editorId],
                },
                true,
              ],
            },
            redo: { action: changeTextEditorStyle, props: [editorId, style, true] },
          });
        if (style.all) newState = EditorState.moveSelectionToEnd(newState);
        return dispatch({
          type: designActionTypes.CHANGE_EDITOR_STATE,
          newEditorState: { [editorId]: newState },
          saved: false,
        });
      }
    } catch (err) {
      return () => { };
    }
  };
};

const changeTextEditorsStyle = (editorIds, style, noCache) => {
  return (dispatch, getState) => {
    try {
      let editorsState = getState().design.editorsState;
      const newEditorsState = _applyToTextGroup(editorIds, editorsState, style);
      if (!noCache) {
        const oldEditorsState = {};
        editorIds.forEach(id => {
          oldEditorsState[id] = getState().design.editorsState[id];
        });
        addChanges({
          undo: {
            action: changeEditorsState,
            props: [oldEditorsState, true, 'undo'],
          },
          redo: { action: changeTextEditorsStyle, props: [editorIds, style, true] },
        });
      }
      return dispatch({
        type: designActionTypes.CHANGE_EDITOR_STATE,
        newEditorState: newEditorsState,
        saved: false,
      });
    } catch (err) {
      return () => { };
    }
  };
};

/**
 *
 * @param {*} newEditorsState
 * @param {Boolean} noCache true for save changes
 */
const changeEditorsState = (newEditorsState, noCache) => {
  return (dispatch, getState) => {
    try {
      const editorIds = Object.keys(newEditorsState);
      const contentChanged =
        newEditorsState[editorIds[0]].getUndoStack().size !==
        getState().design.editorsState[editorIds[0]].getUndoStack().size;
      if (!noCache) {
        if (contentChanged) {
          const oldEditorsState = {};
          editorIds.forEach(id => {
            oldEditorsState[id] = getState().design.editorsState[id];
          });
          addChanges({
            undo: {
              action: changeEditorsState,
              props: [oldEditorsState, true, 'undo'],
            },
            redo: { action: changeEditorsState, props: [newEditorsState, true, 'redo'] },
          });
        }
      }
      return dispatch({
        type: designActionTypes.CHANGE_EDITOR_STATE,
        newEditorState: newEditorsState,
        saved: !contentChanged,
      });
    } catch (err) {
      return () => { };
    }
  };
};

/**
 *
 * @param {*} newSVGState
 */
const changeSVGState = newSVGState => {
  return dispatch => {
    return dispatch({ type: designActionTypes.CHANGE_SVG_STATE, newSVGState });
  };
};

/**
 *
 * @param {*} newImageState
 * @param {Boolean} noCache true for save changes
 */
const changeImageState = (newImageState, noCache) => {
  return (dispatch, getState) => {
    if (!noCache) {
      const imageId = Object.keys(newImageState)[0];
      addChanges({
        undo: {
          action: changeImageState,
          props: [{ [imageId]: getState().design.imagesState[imageId] }, true],
        },
        redo: { action: changeImageState, props: [newImageState, true] },
      });
    }
    return dispatch({ type: designActionTypes.CHANGE_IMAGE_STATE, newImageState });
  };
};

/**
 *
 * @param {*} pageId
 * @param {*} entityId
 * @param {*} style
 * @param {Boolean} noCache true for save changes
 * @param {*} previousStyle  for undo
 */
const changeEntityStyle = (pageId, entityId, style, noCache, previousStyle) => {
  return (dispatch, getState) => {
    try {
      if (entityId === 'Background' || entityId < 0) {
        const pages = [...getState().design.pages];
        if (!noCache)
          addChanges({
            undo: {
              action: changeEntityStyle,
              props: [pageId, entityId, style, true, { ..._.get(pages[pageId], 'background.media.style') }],
            },
            redo: { action: changeEntityStyle, props: [pageId, entityId, style, true] },
          });
        _.set(
          pages[pageId],
          'background.media.style',
          noCache
            ? previousStyle
            : {
              ...pages[pageId].background.media.style,
              ...style,
            },
        );
        return dispatch({
          type: designActionTypes.CHANGE_ENTITY_STYLE,
          pages,
          entityState: {},
        });
      } else {
        let entityState = _.cloneDeep(getState().design.entitiesState[entityId]);
        if (!noCache)
          addChanges({
            undo: {
              action: changeEntityStyle,
              props: [pageId, entityId, style, true, _.cloneDeep(entityState.style)],
            },
            redo: { action: changeEntityStyle, props: [pageId, entityId, style, true] },
          });
        if (previousStyle) entityState.style = previousStyle;
        else _.merge(entityState.style, style);
        return dispatch({
          type: designActionTypes.CHANGE_ENTITY_STYLE,
          pages: getState().design.pages,
          entityState: { [entityId]: entityState },
        });
      }
    } catch (err) {
      return () => { };
    }
  };
};

/**
 *
 * @param {*} pageId
 * @param {Array} groupEntities array of entities
 * @param {Boolean} noCache true for save changes
 * @param {*} previousStyle  for undo
 */
const changeGroupEntityStyle = (pageId, groupEntities, noCache, previousStyle) => {
  return (dispatch, getState) => {
    try {
      const entitiesState = getState().design.entitiesState;
      let newEntitiesState = {};
      let currentStyle = [];
      groupEntities.forEach((groupEntity, index) => {
        currentStyle.push({ id: groupEntity.id, data: entitiesState[groupEntity.id].style });
        newEntitiesState[groupEntity.id] = _.cloneDeep(entitiesState[groupEntity.id]);
        newEntitiesState[groupEntity.id].style = previousStyle
          ? _.get(previousStyle[index], 'data', {})
          : {
            ...entitiesState[groupEntity.id].style,
            ...groupEntity.data,
          };
      });
      if (!noCache)
        addChanges({
          undo: {
            action: changeGroupEntityStyle,
            props: [pageId, groupEntities, true, currentStyle],
          },
          redo: { action: changeGroupEntityStyle, props: [pageId, groupEntities, true] },
        });
      return dispatch({ type: designActionTypes.CHANGE_GROUP_ENTITY_STYLE, newEntitiesState });
    } catch (err) {
      return () => { };
    }
  };
};

/**
 *
 * @param {id, type} entity
 */
const changeSelectedEntity = entity => {
  return (dispatch, getState) => {
    if (
      _.get(getState().design.selectedEntity, 'id') !== _.get(entity, 'id') ||
      _.get(getState().design.selectedEntity, 'type') !== _.get(entity, 'type') ||
      _.get(getState().design.selectedEntity, 'lock') !== _.get(entity, 'lock')
    )
      return dispatch({ type: designActionTypes.CHANGE_SELECTED_ENTITY, entity });
  };
};

/**
 *
 * @param {id, editable} entity
 */
const changeEditableMode = entity => {
  return (dispatch, getState) => {
    if (
      _.get(getState().design.entityEditableMode, 'editable') !== _.get(entity, 'editable') ||
      _.get(getState().design.entityEditableMode, 'id') !== _.get(entity, 'id')
    )
      return dispatch({ type: designActionTypes.CHANGE_ENTITY_EDITABLE_MODE, entity });
  };
};

/**
 *
 * @param {*} pageId
 * @param {*} entityId
 * @param {*} arrange
 * @param {Boolean} noCache true for save changes
 * @param {*} previousIndex for undo
 */
const changeEntityArrange = (pageId, entityId, arrange, noCache, previousIndex) => {
  return (dispatch, getState) => {
    const pages = [...getState().design.pages];
    const entitiesLength = pages[pageId].entities.length;
    let entitiesIndex = [];
    if (Array.isArray(entityId)) {
      entityId.forEach(id => {
        entitiesIndex.push(_.findIndex(pages[pageId].entities, entity => entity.id === id));
      });
    } else {
      entitiesIndex = [_.findIndex(pages[pageId].entities, entity => entity.id === entityId)];
    }
    let entity;
    let previousIndexes = [];
    let overlapIndex;

    switch (arrange) {
      case 'forward':
        entitiesIndex.forEach(entityIndex => {
          if (entityIndex === entitiesLength - 1) {
            return '';
          }
          const tempOverlapIndex = getOverlapEntities(
            pages[pageId].entities[entityIndex].id,
            pages[pageId].entities.slice(entityIndex + 1),
            'forward',
            true,
          );
          if (isNaN(overlapIndex) || tempOverlapIndex[0] + entityIndex + 1 > overlapIndex)
            overlapIndex = tempOverlapIndex[0] + entityIndex + 1;
        });
        if (!isNaN(overlapIndex)) {
          entitiesIndex.sort((a, b) => b - a);
          entitiesIndex.forEach((entityIndex, index) => {
            if (entityIndex === entitiesLength - 1 || entityIndex > overlapIndex) {
              return '';
            }
            entity = pages[pageId].entities.splice(entityIndex, 1);
            pages[pageId].entities.splice(overlapIndex - index, 0, ...entity);

            previousIndexes.push({ newIndex: overlapIndex - index, prevIndex: entityIndex });
          });
          if (!noCache)
            addChanges({
              undo: {
                action: changeEntityArrange,
                props: [pageId, entityId, 'moveToIndex', true, previousIndexes.reverse()],
              },
              redo: { action: changeEntityArrange, props: [pageId, entityId, arrange, true] },
            });
        }
        break;
      case 'backward':
        entitiesIndex.forEach(entityIndex => {
          if (entityIndex === 0) {
            return '';
          }
          const tempOverlapIndex = getOverlapEntities(
            pages[pageId].entities[entityIndex].id,
            pages[pageId].entities.slice(0, entityIndex),
            'backward',
            true,
          );
          if (isNaN(overlapIndex) || tempOverlapIndex[0] < overlapIndex) overlapIndex = tempOverlapIndex[0];
        });
        if (!isNaN(overlapIndex)) {
          entitiesIndex.sort((a, b) => a - b);
          entitiesIndex.forEach((entityIndex, index) => {
            if (entityIndex === 0) {
              return '';
            }
            entity = pages[pageId].entities.splice(entityIndex, 1);
            pages[pageId].entities.splice(overlapIndex + index, 0, ...entity);

            previousIndexes.push({ newIndex: overlapIndex + index, prevIndex: entityIndex });
          });
          if (!noCache)
            addChanges({
              undo: {
                action: changeEntityArrange,
                props: [pageId, entityId, 'moveToIndex', true, previousIndexes.reverse()],
              },
              redo: { action: changeEntityArrange, props: [pageId, entityId, arrange, true] },
            });
        }
        break;
      case 'toFront':
        entitiesIndex.sort((a, b) => b - a);
        entitiesIndex.forEach((entityIndex, index) => {
          entity = pages[pageId].entities.splice(entityIndex, 1);
          pages[pageId].entities.splice(entitiesLength - 1 - index, 0, ...entity);

          previousIndexes.push({ newIndex: entitiesLength - index - 1, prevIndex: entityIndex });
        });
        if (!noCache)
          addChanges({
            undo: {
              action: changeEntityArrange,
              props: [pageId, entityId, 'moveToIndex', true, previousIndexes],
            },
            redo: { action: changeEntityArrange, props: [pageId, entityId, arrange, true] },
          });
        break;
      case 'toBack':
        entitiesIndex.sort((a, b) => a - b);
        entitiesIndex.forEach((entityIndex, index) => {
          entity = pages[pageId].entities.splice(entityIndex, 1);
          pages[pageId].entities.splice(index, 0, ...entity);

          previousIndexes.push({ newIndex: index, prevIndex: entityIndex });
        });
        if (!noCache)
          addChanges({
            undo: {
              action: changeEntityArrange,
              props: [pageId, entityId, 'moveToIndex', true, previousIndexes.reverse()],
            },
            redo: { action: changeEntityArrange, props: [pageId, entityId, arrange, true] },
          });
        break;
      case 'moveToIndex':
        previousIndex.forEach(previous => {
          entity = pages[pageId].entities.splice(previous.newIndex, 1);
          pages[pageId].entities.splice(previous.prevIndex, 0, ...entity);
        });
        break;
      default:
        break;
    }

    return dispatch({ type: designActionTypes.CHANGE_ENTITY_ARRANGE, pages });
  };
};

/**
 *
 * @param {*} entityId
 * @param {array} entities
 * @param {string} direction backward, forward
 * @param {boolean} justFirst
 * @returns
 */
const getOverlapEntities = (entityId, entities, direction, justFirst) => {
  console.log(entityId, entities, direction, justFirst);
  try {
    const indexes = [];
    const rect1 = document.getElementById(`_${entityId}_`).getBoundingClientRect();

    // To find nearest entity for backward action
    if (direction === 'backward')
      for (let i = entities.length - 1; i >= 0; i--) {
        const rect2 = document.getElementById(`_${entities[i].id}_`).getBoundingClientRect();

        if (isOverlap(rect1, rect2)) {
          indexes.push(i);
          if (justFirst) break;
        }
      }
    else if (direction === 'forward')
      for (let i = 0; i < entities.length; i++) {
        const rect2 = document.getElementById(`_${entities[i].id}_`).getBoundingClientRect();

        if (isOverlap(rect1, rect2)) {
          indexes.push(i);
          if (justFirst) break;
        }
      }

    return indexes;
  } catch (error) {
    return [];
  }
};

const isOverlap = (rect1, rect2) =>
  !(rect1.right < rect2.left || rect1.left > rect2.right || rect1.bottom < rect2.top || rect1.top > rect2.bottom);

const changeColorPickerColor = color => {
  return dispatch => dispatch({ type: designActionTypes.CHANGE_COLOR_PICKER_COLOR, color });
};

const changeRectSelection = selectedEntities => {
  return dispatch => dispatch({ type: designActionTypes.CHANGE_RECT_SELECTION, selectedEntities });
};

const setSidePanel = panel => {
  return (dispatch, getState) => {
    if (getState().design.sidePanel !== panel) return dispatch({ type: designActionTypes.SET_SIDE_PANEL, panel });
  };
};

const showFilterPanel = show => {
  return (dispatch, getState) => {
    if (getState().design.showFilterPanel !== show)
      return dispatch({ type: designActionTypes.SHOW_FILTER_PANEL, show });
  };
};

/**
 *
 * @param {*} pageId
 * @param {*} entityId
 * @param {*} indexColor
 * @param {Boolean} noCache true for save changes
 */
const changeSvgColor = (pageId, entityId, indexColor, noCache) => {
  return (dispatch, getState) => {
    const entityState = getState().design.entitiesState[entityId];
    const svgState = [...getState().design.svgsState[entityId]];
    if (!noCache)
      addChanges({
        undo: {
          action: changeSvgColor,
          props: [
            pageId,
            entityId,
            {
              index: indexColor.index,
              fill: svgState[indexColor.index].fill,
              stroke: svgState[indexColor.index].stroke,
            },
            true,
          ],
        },
        redo: {
          action: changeSvgColor,
          props: [pageId, entityId, indexColor, true],
        },
      });
    svgState[indexColor.index].fill = indexColor.fill;
    svgState[indexColor.index].stroke = indexColor.stroke;
    _.set(entityState, 'data.colorList', svgState);
    return dispatch({
      type: designActionTypes.CHANGE_SVG_COLOR,
      entityState: { [entityId]: entityState },
      svgState: { [entityId]: svgState },
    });
  };
};

const addFont = fontName => {
  return (dispatch, getState) => {
    const fontsLoaded = getState().design.fontsLoaded;
    if (!fontsLoaded[fontName]) {
      return dispatch({
        type: designActionTypes.ADD_FONT,
        fontsLoaded: { ...fontsLoaded, [fontName]: fontName },
      });
    }
  };
};

const changeDesignSize = ({ width, height }) => {
  return dispatch => dispatch({ type: designActionTypes.CHANGE_DESIGN_SIZE, width, height });
};

const enableEditor = editorId => {
  return (dispatch, getState) => {
    if (getState().design.enabledEditorId !== editorId)
      return dispatch({ type: designActionTypes.CHANGE_ENABLE_EDITOR, editorId });
  };
};

/**
 *
 * @param {Number} pageId
 * @param {Number} entityId
 * @param {String} resource _id of resource
 * @param {String} src
 * @param {String} type
 * @param {Boolean} noCache true for save changes
 */
const changeEntity = (pageId, entityId, resource, src, type, noCache) => {
  return (dispatch, getState) => {
    const changes = {
      redo: { action: changeEntity, props: [pageId, entityId, resource, src, type, true] },
    };
    const pages = [...getState().design.pages];
    const entitiesState = getState().design.entitiesState;
    if (entityId === 'Background') {
      if (!noCache) {
        if (_.has(pages[pageId], 'background.media')) {
          changes.undo = {
            action: changeEntity,
            props: [
              pageId,
              entityId,
              pages[pageId].background.media.resource,
              pages[pageId].background.media.src,
              pages[pageId].background.media.type,
              true,
            ],
          };
        } else {
          changes.undo = {
            action: deletePageBackground,
            props: [pageId, true],
          };
        }
        addChanges(changes);
      }
      const update = { resource, src, type };
      _.set(pages[pageId], 'background.media', {
        ..._.get(pages[pageId], 'background.media', {}),
        ...update,
      });
      return dispatch({ type: designActionTypes.CHANGE_ENTITY_SRC, pages, entitiesState });
    } else {
      pages[pageId].entities.forEach(entity => {
        if (entity.id === entityId) {
          if (!noCache)
            addChanges({
              undo: {
                action: changeEntity,
                props: [
                  pageId,
                  entityId,
                  entitiesState[entityId].resource,
                  entitiesState[entityId].src,
                  entitiesState[entityId].type,
                  true,
                ],
              },
              redo: { action: changeEntity, props: [pageId, entityId, resource, src, type, true] },
            });
          return (entity.type = type);
        }
      });
      entitiesState[entityId].resource = resource;
      entitiesState[entityId].src = src;
      entitiesState[entityId].type = type;
      return dispatch({ type: designActionTypes.CHANGE_ENTITY_SRC, pages, entitiesState });
    }
  };
};

/**
 *
 * @param {Number} pageId
 * @param {Number} entityId
 * @param {Array} data
 * @param {String} type
 * @param {Boolean} noCache true for save changes
 * @param {*} previousData
 */
const changeGridData = (pageId, entityId, data, noCache, previousData) => {
  return (dispatch, getState) => {
    let entityState = _.cloneDeep(getState().design.entitiesState[entityId]);
    if (!noCache)
      addChanges({
        undo: {
          action: changeGridData,
          props: [pageId, entityId, data, true, entityState.data],
        },
        redo: { action: changeGridData, props: [pageId, entityId, data, true] },
      });
    // const updateData = { medias: _.merge(entityState.data.medias, data.medias) };
    // const style = { ...entityState.data.medias.style, ...data.medias.style };
    const medias = { ...entityState.data.medias, ...data.medias };
    const template = { ...entityState.data.template, ...data.template };
    const updateData = { medias: medias, template: template };
    entityState.data = previousData ? previousData : { ...entityState.data, ...updateData };

    return dispatch({
      type: designActionTypes.CHANGE_GRID_DATA,
      entityState: { [entityId]: entityState },
    });
  };
};

/**
 *
 * @param {Number} pageId
 * @param {Number} entityId
 * @param {Array} data
 * @param {String} type
 * @param {Boolean} noCache true for save changes
 * @param {*} previousData
 */
const changeFrameData = (pageId, entityId, data, noCache, previousData) => {
  return (dispatch, getState) => {
    const entitiesState = getState().design.entitiesState;
    if (!noCache)
      addChanges({
        undo: {
          action: changeFrameData,
          props: [pageId, entityId, data, true, entitiesState[entityId].data],
        },
        redo: { action: changeFrameData, props: [pageId, entityId, data, true] },
      });
    // const updateData = { medias: _.merge(entitiesState[entityId].data.medias, data.medias) };
    const style = { ..._.get(entitiesState[entityId], 'data.medias.style'), ...data.medias.style };
    const frame = { ..._.get(entitiesState[entityId], 'data.medias.frame'), ...data.medias.frame };
    const medias = { style: style, frame: frame };
    const template = { ...entitiesState[entityId].data.template, ...data.template };
    const updateData = { medias: medias, template: template };
    entitiesState[entityId].data = previousData ? previousData : { ...entitiesState[entityId].data, ...updateData };

    return dispatch({ type: designActionTypes.CHANGE_FRAME_DATA, entitiesState });
  };
};

/**
 * Didn't change immutable state
 * @param {Number} pageId
 * @param {String} color
 * @param {Boolean} noCache true for save changes
 */
const changePageBackgroundColor = (pageId, color, noCache) => {
  return (dispatch, getState) => {
    const changes = { redo: { action: changePageBackgroundColor, props: [pageId, color, true] } };
    const pages = [...getState().design.pages];
    if (!noCache) {
      if (_.has(pages[pageId], 'background.color')) {
        changes.undo = {
          action: changePageBackgroundColor,
          props: [pageId, pages[pageId].background.color],
        };
      } else {
        changes.undo = {
          action: deletePageBackground,
          props: [pageId, true],
        };
      }
      addChanges(changes);
    }
    _.set(pages[pageId], 'background.color', color);
    return dispatch({ type: designActionTypes.CHANGE_PAGE_BACKGROUND_COLOR, pages });
  };
};

/**
 * Didn't change immutable state
 * @param {Number} pageId
 * @param {Number} entityId
 * @param {Object} filter
 * @param {Boolean} noCache true for save changes
 */
const changeImageFilter = (pageId, entityId, filter, noCache) => {
  let previousFilter;
  return (dispatch, getState) => {
    const entitiesState = getState().design.entitiesState;
    const pages = getState().design.pages;
    if (typeof entityId === 'string') {
      previousFilter = _.get(pages[pageId], 'background.media.filter', '6464646464646432');
      pages[pageId].background.media.filter = filter;
    } else {
      previousFilter = entitiesState[entityId].style.filter;
      entitiesState[entityId].style.filter = filter;
    }
    if (!noCache)
      addChanges({
        undo: {
          action: changeImageFilter,
          props: [pageId, entityId, previousFilter, true],
        },
        redo: { action: changeImageFilter, props: [pageId, entityId, filter, true] },
      });
    return dispatch({ type: designActionTypes.CHANGE_IMAGE_FILTER, entitiesState });
  };
};

/**
 * Didn't change immutable state
 * @param {Number} pageId
 * @param {Number} entityId
 * @param {Object} data
 * @param {Boolean} noCache true for save changes
 * @param {*} previousData
 */
const changeEntityData = (pageId, entityId, data, noCache, previousData) => {
  return (dispatch, getState) => {
    if (entityId === 'Background' || typeof entityId === 'string') {
      const pages = [...getState().design.pages];
      if (!noCache)
        addChanges({
          undo: {
            action: changeEntityData,
            props: [pageId, entityId, data, true, _.get(pages[pageId], 'background.media.data')],
          },
          redo: { action: changeEntityData, props: [pageId, entityId, data, true] },
        });
      const update = _.merge(pages[pageId].background.media, { data: data });

      _.set(
        pages[pageId],
        'background.media',
        previousData
          ? previousData
          : {
            ..._.get(pages[pageId], 'background.media', {}),
            ...update,
          },
      );
      return dispatch({
        type: designActionTypes.CHANGE_ENTITY_DATA,
        pages,
        entityState: {},
      });
    } else {
      const entitiesState = getState().design.entitiesState;
      if (!noCache)
        addChanges({
          undo: {
            action: changeEntityData,
            props: [pageId, entityId, data, true, _.get(entitiesState[entityId], 'data', {})],
          },
          redo: { action: changeEntityData, props: [pageId, entityId, data, true] },
        });
      entitiesState[entityId].data = previousData
        ? previousData
        : { ..._.get(entitiesState[entityId], 'data', {}), ...data };
      return dispatch({
        type: designActionTypes.CHANGE_ENTITY_DATA,
        pages: getState().design.pages,
        entitiesState,
      });
    }
  };
};

const changeDesignScale = scale => {
  return dispatch => dispatch({ type: designActionTypes.CHANGE_DESIGN_PAGE_SCALE, scale });
};

/**
 *
 * @param {String} name
 * @param {Boolean} noCache true for save changes
 */
const changeName = (name, noCache) => {
  return (dispatch, getState) => {
    if (getState().design.name !== name) {
      if (!noCache)
        addChanges({
          undo: {
            action: changeName,
            props: [getState().design.name, true],
          },
          redo: { action: changeName, props: [name, true] },
        });
      return dispatch({ type: designActionTypes.CHANGE_NAME, name });
    }
  };
};

const exportDesign = (designId, data, type, sourceType) => {
  return (dispatch, getState) => {
    dispatch(request());

    if (!designId) {
      dispatch({
        type: designActionTypes.EXPORT_FAILURE,
        error: { status: 400, error: 'A valid export target is required' },
      });
      return;
    }

    const user = getState().authentication.user;
    let userId = null;
    if (user.role === 'client') {
      userId = user.userId;
    }

    const spacingStyleClasses = getState().design.spacingStyleClasses;
    const listBlockStyleClasses = getState().design.listBlockStyleClasses;
    data.pages = data.pages.map(page => {
      return `<style>${Object.values(spacingStyleClasses).join(' ')}${Object.values(listBlockStyleClasses).join(
        ' ',
      )}</style>${page}`;
    });

    const exportRequest = sourceType === 'template'
      ? templateService.export(designId, data, { type })
      : designService.export(userId, designId, data, { type });

    exportRequest.then(
      response => {
        dispatch(success(response.path));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            designActionTypes.EXPORT_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: designActionTypes.EXPORT_REQUEST };
  }
  function success(path) {
    return { type: designActionTypes.EXPORT_SUCCESS, path };
  }
};

const deleteDesign = designId => {
  return (dispatch, getState) => {
    dispatch(request());

    const userId = getState().authentication.user.userId;
    designService.delete(userId, designId).then(
      () => {
        const designs = [...getState().design.designs];
        _.remove(designs, design => {
          return design._id === designId;
        });
        dispatch(success(designs));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            designActionTypes.DELETE_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: designActionTypes.DELETE_REQUEST };
  }
  function success(designs) {
    return { type: designActionTypes.DELETE_SUCCESS, designs };
  }
};

const undo = () => {
  const undo = getUndo();
  if (!undo) return () => { };
  return undo.action(...undo.props);
};

const redo = () => {
  const redo = getRedo();
  if (!redo) return () => { };
  return redo.action(...redo.props);
};

/**
 * @param {Number} pageId
 * @param {Array} entityIds
 * @param {Boolean} noCache true for save changes
 * @param {Array} previousKeyIds
 */
const duplicateEntity = (pageId, entityIds, noCache, previousKeyIds) => {
  return (dispatch, getState) => {
    const pages = [...getState().design.pages];
    const entitiesState = getState().design.entitiesState;
    const imagesState = getState().design.imagesState;
    const svgsState = getState().design.svgsState;
    const editorsState = getState().design.editorsState;
    const width = getState().design.width;
    const height = getState().design.height;
    const scale = getState().design.scale;
    let lastEntity;

    const newEntityIds = [];
    entityIds.forEach((entityId, index) => {
      const keyId = parseInt(_.get(previousKeyIds, `[${index}]`) || window.$_getEntityKey());
      newEntityIds.push(keyId.toString());
      let newEntity = _.cloneDeep(entitiesState[entityId]);
      let transform = _.get(newEntity, 'style.master.transform', 'translateX(0px) translateY(0px)');
      transform = transform.replace(/(?:translateX\()([0-9]+)/, (n, m) => {
        const x = (width * scale) / 100 > parseInt(m, 10) + 50 ? parseInt(m, 10) + 50 : parseInt(m, 10) - 50;
        return `translateX(${x}`;
      });
      transform = transform.replace(/(?:translateY\()([0-9]+)/, (n, m) => {
        const y = (height * scale) / 100 > parseInt(m, 10) + 50 ? parseInt(m, 10) + 50 : parseInt(m, 10) - 50;
        return `translateY(${y}`;
      });
      _.set(newEntity, 'style.master.transform', transform);
      newEntity.keyId = keyId;

      entitiesState[keyId] = newEntity;
      if (newEntity.type === 'Image') {
        imagesState[keyId] = _.cloneDeep(imagesState[entityId]);
      } else if (newEntity.type === 'Vector' || newEntity.type === 'Shape') {
        svgsState[keyId] = _.cloneDeep(svgsState[entityId]);
      } else if (newEntity.type === 'Text') {
        editorsState[keyId] = _.cloneDeep(editorsState[entityId]);
      }
      pages[pageId].entities = [...pages[pageId].entities, { id: keyId, type: newEntity.type }];
      lastEntity = { id: keyId, type: newEntity.type };
    });

    if (!noCache)
      addChanges({
        undo: { action: deleteEntity, props: [pageId, newEntityIds, true] },
        redo: { action: duplicateEntity, props: [pageId, entityIds, true, newEntityIds] },
      });
    return dispatch({
      type: designActionTypes.DUPLICATE_ENTITY,
      pages,
      entitiesState,
      imagesState,
      svgsState,
      editorsState,
      selectedEntity: lastEntity,
    });
  };
};

const addSpacingStyleClass = (letterSpacing, lineHeight) => {
  return (dispatch, getState) => {
    const spacingStyleClasses = getState().design.spacingStyleClasses;
    if (!spacingStyleClasses[`letterSpacing-${letterSpacing}`] || !spacingStyleClasses[`lineHeight-${lineHeight}`]) {
      return dispatch({
        type: designActionTypes.ADD_SPACING_STYLE_CLASS,
        spacingStyleClasses: {
          ...spacingStyleClasses,
          [`letterSpacing-${letterSpacing}`]: `.letterSpacing_${letterSpacing
            .toString()
            .replace('.', '-')} {letter-spacing: ${letterSpacing}em}`,
          [`lineHeight-${lineHeight}`]: `.lineHeight_${lineHeight
            .toString()
            .replace('.', '-')} {line-height: ${lineHeight}}`,
        },
      });
    }
  };
};

const addListBlockStyleClass = ({ fontsize, color }) => {
  return (dispatch, getState) => {
    const listBlockStyleClasses = getState().design.listBlockStyleClasses;
    if (!listBlockStyleClasses[`listBlock-${fontsize.replace('.', '_')}-${color}`]) {
      return dispatch({
        type: designActionTypes.ADD_LIST_BLOCK_STYLE_CLASS,
        listBlockStyleClasses: {
          ...listBlockStyleClasses,
          [`listBlock-${fontsize.replace('.', '_')}-${color}`]: `.listBlock-${fontsize.replace(
            '.',
            '_',
          )}-${color} {font-size: ${fontsize}px; color: #${color}}`,
        },
      });
    }
  };
};

const changeReplaceEntityState = replaceEntity => {
  return dispatch => dispatch({ type: designActionTypes.REPLACE_ENTITY_STATE, replaceEntity });
};

const setCurrentTextStyle = ({
  fontSize,
  fontFamily,
  fontColor,
  inlineStyles,
  textBlockType,
  textAlignment,
  textTransform,
  letterSpacing,
  lineHeight,
}) => {
  return dispatch => {
    return dispatch({
      type: designActionTypes.SET_CURRENT_TEXT_INLINE_STYLE,
      fontSize,
      fontFamily,
      fontColor,
      inlineStyles,
      textBlockType,
      textAlignment,
      textTransform,
      letterSpacing,
      lineHeight,
    });
  };
};

const changeEntityLockState = (entityId, lock) => {
  return (dispatch, getState) => {
    const entitiesState = getState().design.entitiesState;
    if (entityId.toString().includes('-')) {
      let pages = [...getState().design.pages];
      const backgroundId = parseInt(entityId.replace('-', ''));
      _.set(pages[backgroundId], 'background.lock', lock);
      pages = [...pages];
      return (
        dispatch({
          type: designActionTypes.CHANGE_SELECTED_ENTITY,
          entity: { ...getState().design.selectedEntity, lock: lock },
        }),
        dispatch({
          type: designActionTypes.CHANGE_PAGE_BACKGROUND_LOCK_STATE,
          pages: [...pages],
        })
      );
    } else if (Array.isArray(entityId)) {
      entityId.forEach(elementId => {
        if (entitiesState[elementId].lock !== lock) {
          entitiesState[elementId] = { ...entitiesState[elementId], lock: lock };
        }
      });
      return (
        dispatch({
          type: designActionTypes.CHANGE_SELECTED_ENTITY,
          entity: { ...getState().design.selectedEntity, lock: lock },
        }),
        dispatch({
          type: designActionTypes.CHANGE_LOCK_STATE,
          entitiesState: { ...entitiesState },
        })
      );
    } else {
      if (entitiesState[entityId].lock !== lock) {
        entitiesState[entityId] = { ...entitiesState[entityId], lock: lock };
        return (
          dispatch({
            type: designActionTypes.CHANGE_SELECTED_ENTITY,
            entity: { ...getState().design.selectedEntity, lock: lock },
          }),
          dispatch({
            type: designActionTypes.CHANGE_LOCK_STATE,
            entitiesState: { ...entitiesState },
          })
        );
      }
    }
  };
};

const _extractFontFamily = () => {
  let fontsLoaded = {};
  let font;
  const editorsCustomStyleMap = getCustomStyleMap();
  Object.keys(editorsCustomStyleMap).forEach(key => {
    if (key.includes('fontfamily')) {
      font = key.replace('fontfamily-', '');
      fontsLoaded[font] = font;
    }
  });

  return fontsLoaded;
};

const _setEditorFontStyle = (editorState, { fontSize, fontFamily, fontStyle, color }) => {
  let selection = undefined;
  let currentContent = editorState.getCurrentContent();
  const undoStack = _.cloneDeep(editorState.getUndoStack());

  // Select All
  selection = editorState.getSelection().merge({
    anchorKey: currentContent.getFirstBlock().getKey(),
    anchorOffset: 0,
    focusOffset: currentContent.getLastBlock().getText().length,
    focusKey: currentContent.getLastBlock().getKey(),
  });

  editorState = EditorState.forceSelection(editorState, selection);
  editorState = fontFamily ? toggleCustomInlineStyle(editorState, 'fontFamily', fontFamily) : editorState;
  editorState = fontSize
    ? toggleCustomInlineStyle(editorState, 'fontSize', parseInt(fontSize, 10) / 0.75)
    : editorState;
  editorState = color ? toggleCustomInlineStyle(editorState, 'color', color) : editorState;
  fontStyle &&
    fontStyle.forEach(style => {
      editorState = RichUtils.toggleInlineStyle(editorState, style);
    });

  editorState = EditorState.set(editorState, { undoStack });
  editorState = EditorState.moveSelectionToEnd(editorState);

  return editorState;
};

const _refreshTokenOnFailure = (error, failureAction, pendingRefreshToken) => {
  if (error.error === 'jwt expired') {
    if (pendingRefreshToken) {
      return {
        type: userActionTypes.PENDING_REFRESH_TOKEN,
        error,
      };
    } else {
      return { type: userActionTypes.INVALID_TOKEN, error };
    }
  } else {
    return { type: failureAction, error };
  }
};

const _parseDesign = design => {
  const pages = [];
  let entitiesState = {};
  let editorsState = {};
  let svgsState = {};
  let imagesState = {};
  const _parseType = (entity, keyId) => {
    let editorRawTemp = {};
    switch (entity.type) {
      case 'Image':
        imagesState = {
          ...imagesState,
          [keyId]: entity.filter,
        };
        entitiesState = {
          ...entitiesState,
          [keyId]: {
            _id: entity._id,
            type: entity.type,
            keyId,
            style: entity.style,
            ...entity.data,
            lock: entity.lock,
          },
        };
        break;
      case 'Video':
        entitiesState = {
          ...entitiesState,
          [keyId]: {
            _id: entity._id,
            type: entity.type,
            keyId,
            style: entity.style,
            data: entity.data,
            src: entity.data.src,
            lock: entity.lock,
          },
        };
        break;
      case 'Shape':
        svgsState = {
          ...svgsState,
          [keyId]: [...entity.data.colorList],
        };
        entitiesState = {
          ...entitiesState,
          [keyId]: {
            _id: entity._id,
            type: entity.type,
            keyId,
            style: entity.style,
            ...entity.data,
            lock: entity.lock,
          },
        };
        break;
      case 'Vector':
        svgsState = {
          ...svgsState,
          [keyId]: [...entity.data.colorList],
        };
        entitiesState = {
          ...entitiesState,
          [keyId]: {
            _id: entity._id,
            type: entity.type,
            keyId,
            style: entity.style,
            ...entity.data,
            lock: entity.lock,
          },
        };
        break;
      case 'Frame':
        entitiesState = {
          ...entitiesState,
          [keyId]: {
            _id: entity._id,
            type: entity.type,
            keyId,
            style: entity.style,
            data: entity.data,
            lock: entity.lock,
          },
        };
        break;
      case 'Grid':
        entitiesState = {
          ...entitiesState,
          [keyId]: {
            _id: entity._id,
            type: entity.type,
            keyId,
            style: entity.style,
            data: entity.data,
            lock: entity.lock,
          },
        };
        break;
      case 'Line':
        entitiesState = {
          ...entitiesState,
          [keyId]: {
            _id: entity._id,
            type: entity.type,
            keyId,
            style: entity.style,
            data: entity.data,
            src: entity.data.src,
            lock: entity.lock,
          },
        };
        break;
      case 'Text':
        editorRawTemp = JSON.parse(entity.data.raw);
        editorRawTemp.blocks = editorRawTemp.blocks.map(block => {
          block.inlineStyleRanges = block.inlineStyleRanges.map(inlineStyle => {
            if (inlineStyle.style.includes('fontfamily-')) inlineStyle.style = inlineStyle.style.replaceAll(' ', '__');
            return inlineStyle;
          });
          return block;
        });
        editorsState = {
          ...editorsState,
          [keyId]: EditorState.createWithContent(convertFromRaw(editorRawTemp)),
        };
        extractInlineStyle(editorsState[keyId]);
        entitiesState = {
          ...entitiesState,
          [keyId]: {
            _id: entity._id,
            type: entity.type,
            keyId,
            style: entity.style,
            lock: entity.lock,
          },
        };
        break;
      default:
        break;
    }
  };
  design.pages.forEach(page => {
    const entities = [];
    const entitiesGrouped = _.groupBy(page.entities, function (group) {
      return group.data.groupId;
    });
    for (const [groupName, groupEntities] of Object.entries(entitiesGrouped)) {
      if (groupName && groupName === 'undefined') {
        for (const entity of groupEntities) {
          const entityKeyId = window.$_getEntityKey();
          _parseType(entity, entityKeyId);
          entitiesState[entityKeyId].resource = entity.resource;
          entities.push({ id: entityKeyId, type: entity.type });
        }
      } else {
        // keyId = window.$_getEntityKey();
        const groupEntityIds = [];
        for (const entity of groupEntities) {
          const entityKeyId = window.$_getEntityKey();
          _parseType(entity, entityKeyId);
          entitiesState[entityKeyId].resource = entity.resource;
          groupEntityIds.push({ id: entityKeyId, type: entity.type });
          entities.push({
            id: entityKeyId,
            type: entity.type,
            groupId: groupName,
            entities: groupEntityIds,
          });
        }
        // entities.push({ id: keyId, type: 'Group', groupId: groupName, entities: groupEntityIds });
      }
    }
    pages.push({
      _id: page._id,
      background: page.background,
      entities,
    });
  });

  if (!pages.length) {
    pages.push({ entities: [] });
  }

  // Add used font-family to fontsLoaded
  const fontsLoaded = _extractFontFamily();

  return [
    design.name,
    design.size.width,
    design.size.height,
    pages,
    entitiesState,
    editorsState,
    svgsState,
    imagesState,
    fontsLoaded,
  ];
};

const _packDesign = (id, design) => {
  const designObj = {
    id,
    name: design.name,
    size: { width: design.width, height: design.height },
    pages: [],
  };
  design.pages.forEach(page => {
    const entities = [];
    page.entities.forEach(item => {
      const entity = design.entitiesState[item.id];
      switch (entity.type) {
        case 'Image':
          entities.push({
            _id: entity._id,
            type: entity.type,
            style: entity.style,
            filter: entity.filter,
            data: {
              src: entity.src,
              groupId: entity.groupId,
            },
            lock: entity.lock,
            resource: entity.resource,
          });
          break;
        case 'Video':
          entities.push({
            _id: entity._id,
            type: entity.type,
            style: entity.style,
            data: {
              src: entity.src,
              ...entity.data,
              groupId: entity.groupId,
            },
            lock: entity.lock,
            resource: entity.resource,
          });
          break;
        case 'Shape':
          entities.push({
            _id: entity._id,
            type: entity.type,
            style: entity.style,
            colorList: entity.colorList,
            data: {
              src: entity.src,
              colorList: design.svgsState[entity.keyId],
              groupId: entity.groupId,
            },
            lock: entity.lock,
            resource: entity.resource,
          });
          break;
        case 'Vector':
          entities.push({
            _id: entity._id,
            type: entity.type,
            style: entity.style,
            data: {
              src: entity.src,
              colorList: design.svgsState[entity.keyId],
              groupId: entity.groupId,
            },
            lock: entity.lock,
            resource: entity.resource,
          });
          break;
        case 'Frame':
          entities.push({
            _id: entity._id,
            type: entity.type,
            style: entity.style,
            data: {
              ...entity.data,
              groupId: entity.groupId,
            },
            lock: entity.lock,
            resource: entity.resource,
          });
          break;
        case 'Grid':
          entities.push({
            _id: entity._id,
            type: entity.type,
            style: entity.style,
            data: {
              ...entity.data,
              groupId: entity.groupId,
            },
            lock: entity.lock,
            resource: entity.resource,
          });
          break;
        case 'Line':
          entities.push({
            _id: entity._id,
            type: entity.type,
            style: entity.style,
            data: { src: entity.src, ...entity.data, groupId: entity.groupId },
            lock: entity.lock,
            resource: entity.resource,
          });
          break;
        case 'Text':
          entities.push({
            _id: entity._id,
            type: entity.type,
            style: entity.style,
            data: {
              raw: JSON.stringify(convertToRaw(design.editorsState[entity.keyId].getCurrentContent())),
              groupId: entity.groupId,
            },
            lock: entity.lock,
          });
          break;
        default:
          break;
      }
    });
    designObj.pages.push({ _id: page._id, background: page.background, entities });
  });

  return designObj;
};

const _duplicatePage = (page, entitiesState, editorsState, svgsState, imagesState) => {
  const newPages = [];
  let newEntitiesState = {};
  let newEditorsState = {};
  let newSvgsState = {};
  let newImagesState = {};
  const entities = [];
  page.entities.forEach(entity => {
    const keyId = window.$_getEntityKey();
    switch (entity.type) {
      case 'Image':
        newImagesState = {
          ...newImagesState,
          [keyId]: imagesState[entity.id],
        };
        newEntitiesState = {
          ...newEntitiesState,
          [keyId]: { ...entitiesState[entity.id] },
        };
        delete newEntitiesState[keyId]._id;
        break;
      case 'Video':
        newEntitiesState = {
          ...newEntitiesState,
          [keyId]: { ...entitiesState[entity.id] },
        };
        delete newEntitiesState[keyId]._id;
        break;
      case 'Shape':
        newSvgsState = {
          ...newSvgsState,
          [keyId]: _.cloneDeep(svgsState[entity.id]),
        };
        newEntitiesState = {
          ...newEntitiesState,
          [keyId]: { ...entitiesState[entity.id] },
        };
        delete newEntitiesState[keyId]._id;
        break;
      case 'Vector':
        newSvgsState = {
          ...newSvgsState,
          [keyId]: _.cloneDeep(svgsState[entity.id]),
        };
        newEntitiesState = {
          ...newEntitiesState,
          [keyId]: { ...entitiesState[entity.id] },
        };
        delete newEntitiesState[keyId]._id;
        break;
      case 'Frame':
        newEntitiesState = {
          ...newEntitiesState,
          [keyId]: { ...entitiesState[entity.id] },
        };
        delete newEntitiesState[keyId]._id;
        break;
      case 'Grid':
        newEntitiesState = {
          ...newEntitiesState,
          [keyId]: { ...entitiesState[entity.id] },
        };
        delete newEntitiesState[keyId]._id;
        break;
      case 'Line':
        newEntitiesState = {
          ...newEntitiesState,
          [keyId]: { ...entitiesState[entity.id] },
        };
        delete newEntitiesState[keyId]._id;
        break;
      case 'Text':
        newEditorsState = {
          ...newEditorsState,
          [keyId]: EditorState.createWithContent(editorsState[entity.id].getCurrentContent()),
        };
        newEntitiesState = {
          ...newEntitiesState,
          [keyId]: { ...entitiesState[entity.id] },
        };
        delete newEntitiesState[keyId]._id;
        break;
      default:
        break;
    }
    entities.push({ id: keyId, type: entity.type });
  });
  newPages.push({
    background: _.cloneDeep(page.background),
    entities,
  });

  return [newPages, newEntitiesState, newEditorsState, newSvgsState, newImagesState];
};

const _selectAllText = editorState => {
  const currentContent = editorState.getCurrentContent();
  const selectionState = SelectionState.createEmpty(currentContent.getFirstBlock().getKey());
  const selection = selectionState.merge({
    anchorKey: currentContent.getFirstBlock().getKey(),
    anchorOffset: 0,
    focusOffset: currentContent.getLastBlock().getText().length,
    focusKey: currentContent.getLastBlock().getKey(),
    hasFocus: true,
  });
  return EditorState.forceSelection(editorState, selection);
};

const _applyToTextGroup = (ids, editorsState, style) => {
  const newEditorsState = {};
  ids.forEach(id => {
    newEditorsState[id] = _selectAllText(editorsState[id]);
    let newState;
    switch (style.type) {
      case 'fontSize':
        newState = toggleCustomInlineStyle(newEditorsState[id], 'fontSize', style.value);
        break;
      case 'fontFamily':
        newState = toggleCustomInlineStyle(newEditorsState[id], 'fontFamily', style.value);
        break;
      case 'fontColor':
        newState = toggleCustomInlineStyle(newEditorsState[id], 'color', style.value);
        break;
      case 'inline':
        newState = RichUtils.toggleInlineStyle(newEditorsState[id], style.value);
        break;
      case 'list':
        newState = RichUtils.toggleBlockType(newEditorsState[id], style.value);
        break;
      case 'alignment':
        newState = EditorState.push(
          newEditorsState[id],
          Modifier.mergeBlockData(newEditorsState[id].getCurrentContent(), newEditorsState[id].getSelection(), {
            'text-align': style.value,
          }),
          'change-block-data',
        );
        break;
      case 'uppercase':
        newState = EditorState.push(
          newEditorsState[id],
          Modifier.mergeBlockData(newEditorsState[id].getCurrentContent(), newEditorsState[id].getSelection(), {
            'text-transform': style.value,
          }),
          'change-block-data',
        );
        break;
        default:
          break;
    }
    if (newState !== newEditorsState[id]) {
      newState = EditorState.moveSelectionToEnd(newState);
      newEditorsState[id] = newState;
    }
  });

  return newEditorsState;
};

export const designActions = {
  resetState,
  create,
  duplicate,
  save,
  fetch,
  search,
  fetchTemplateDesign,
  fetchTextDesign,
  actionOnPage,
  addEntity,
  deleteEntity,
  groupEntities,
  deletePageBackground,
  addNewEditorState,
  addNewSVGState,
  changeEditorState,
  changeEditorsState,
  changeSVGState,
  changeImageState,
  changeEntityStyle,
  changeGroupEntityStyle,
  changeEntityData,
  changeSelectedEntity,
  changeEditableMode,
  changeEntityArrange,
  changeColorPickerColor,
  changeRectSelection,
  changeSvgColor,
  changeGridData,
  changeFrameData,
  changeImageFilter,
  setSidePanel,
  showFilterPanel,
  loadTemplateDesign,
  loadTextDesign,
  saveAsTemplate,
  saveAsText,
  addFont,
  changeDesignSize,
  enableEditor,
  changeEntity,
  changePageBackgroundColor,
  changeDesignScale,
  changeName,
  exportDesign,
  deleteDesign,
  undo,
  redo,
  duplicateEntity,
  addSpacingStyleClass,
  addListBlockStyleClass,
  changeReplaceEntityState,
  setCurrentTextStyle,
  changeTextEditorStyle,
  changeTextEditorsStyle,
  changeEntityLockState,
  addNewColorState,
};
